import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import {
  fetchCoverageSnapshot, fetchCrossExchangeMap, fetchInferenceChain,
  fetchCustomQuestions, createCustomQuestion, patchCustomQuestion, deleteCustomQuestion,
  type CustomProfileQuestion, type AttributeWithProvenance, type CrossExchangeEntry,
} from '../../lib/adminApi';

type TabKey = 'coverage' | 'cross_exchange' | 'member_lookup' | 'custom_questions';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'coverage',          label: 'Coverage' },
  { key: 'cross_exchange',    label: 'Cross-Exchange Map' },
  { key: 'member_lookup',     label: 'Member Lookup' },
  { key: 'custom_questions',  label: 'Custom Questions' },
];

export default function ProfileIntelligencePage() {
  const [tab, setTab] = useState<TabKey>('coverage');

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold font-display text-white">Profile Intelligence</h1>

      <div className="flex gap-1 border-b border-slate-800">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? 'border-primary text-white font-medium'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'coverage'         && <CoverageTab />}
      {tab === 'cross_exchange'   && <CrossExchangeTab />}
      {tab === 'member_lookup'    && <MemberLookupTab />}
      {tab === 'custom_questions' && <CustomQuestionsTab />}
    </div>
  );
}

// ── Tab 1 — Coverage ─────────────────────────────────────────────────────────

function CoverageTab() {
  const { data } = useQuery({ queryKey: ['admin', 'pi', 'coverage'], queryFn: fetchCoverageSnapshot });
  if (!data) return <div className="text-slate-400 text-sm">Loading…</div>;

  const tenPlusPct = data.total_members > 0
    ? Math.round(((data.coverage_buckets['11-20'] + data.coverage_buckets['21+']) / data.total_members) * 100)
    : 0;

  const sourceData = Object.entries(data.source_breakdown).map(([source, count]) => ({ source, count }));
  const bucketData = (Object.entries(data.coverage_buckets) as Array<[string, number]>)
    .map(([bucket, count]) => ({ bucket, count }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Total Members" value={data.total_members.toLocaleString()} />
        <Stat label="Avg Attributes/Member" value={data.avg_attribute_count.toFixed(1)} />
        <Stat label="% with 10+ Attributes" value={`${tenPlusPct}%`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-xl p-5">
          <h2 className="text-sm font-bold text-white mb-4">Source Breakdown</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={sourceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="source" tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', color: '#fff', fontSize: 12 }} />
              <Bar dataKey="count" fill="#5B4FE8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-slate-800 rounded-xl p-5">
          <h2 className="text-sm font-bold text-white mb-4">Coverage Distribution</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={bucketData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="bucket" tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', color: '#fff', fontSize: 12 }} />
              <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-slate-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-700">
          <h2 className="text-sm font-bold text-white">Top Missing Attributes</h2>
          <p className="text-xs text-slate-400 mt-1">Sorted by gap score (surveys requiring ÷ members having). Higher = bigger collection priority.</p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-900/50 text-[10px] uppercase tracking-wide text-slate-400">
            <tr>
              <th className="text-left p-2.5 font-medium">Attribute</th>
              <th className="text-right p-2.5 font-medium">Surveys requiring</th>
              <th className="text-right p-2.5 font-medium">Members having</th>
              <th className="text-right p-2.5 font-medium">Gap score</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/60">
            {data.top_missing_attrs.map((row) => (
              <tr key={row.attribute_key} className="hover:bg-slate-700/30">
                <td className="p-2.5 text-white font-mono text-xs">{row.attribute_key}</td>
                <td className="p-2.5 text-right text-slate-300 tabular-nums">{row.surveys_requiring}</td>
                <td className="p-2.5 text-right text-slate-300 tabular-nums">{row.members_having}</td>
                <td className="p-2.5 text-right text-amber-400 font-medium tabular-nums">{row.gap_score.toFixed(2)}</td>
              </tr>
            ))}
            {data.top_missing_attrs.length === 0 && (
              <tr><td colSpan={4} className="p-4 text-center text-slate-500 text-xs">No missing attributes yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-800 rounded-xl p-5">
      <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-white mt-1 tabular-nums">{value}</p>
    </div>
  );
}

// ── Tab 2 — Cross-Exchange ───────────────────────────────────────────────────

function CrossExchangeTab() {
  const { data } = useQuery({ queryKey: ['admin', 'pi', 'cross-exchange'], queryFn: fetchCrossExchangeMap });
  const [filter, setFilter] = useState<'all' | 'ps' | 'rfg'>('all');

  if (!data) return <div className="text-slate-400 text-sm">Loading…</div>;

  const entries: CrossExchangeEntry[] = data.entries.filter((e) => {
    if (filter === 'ps')  return e.ps_survey_count > 0;
    if (filter === 'rfg') return e.rfg_survey_count > 0;
    return true;
  });

  const coverageColor = (pct: number) => pct > 70 ? 'text-emerald-400' : pct >= 40 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {(['all', 'ps', 'rfg'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1 rounded-full border ${
              filter === f ? 'bg-slate-700 text-white border-slate-600' : 'text-slate-400 border-slate-700 hover:text-slate-200'
            }`}
          >{f === 'all' ? 'All' : f.toUpperCase()} only</button>
        ))}
      </div>
      <div className="bg-slate-800 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/50 text-[10px] uppercase tracking-wide text-slate-400">
            <tr>
              <th className="text-left p-2.5 font-medium">Attribute</th>
              <th className="text-right p-2.5 font-medium">PS Qual</th>
              <th className="text-right p-2.5 font-medium">PS Surveys</th>
              <th className="text-left p-2.5 font-medium">RFG Qual Names</th>
              <th className="text-right p-2.5 font-medium">RFG Surveys</th>
              <th className="text-right p-2.5 font-medium">Total</th>
              <th className="text-right p-2.5 font-medium">Coverage %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/60">
            {entries.map((e) => (
              <tr key={e.attribute_key} className="hover:bg-slate-700/30">
                <td className="p-2.5 text-white font-mono text-xs">{e.attribute_key}</td>
                <td className="p-2.5 text-right text-slate-300 tabular-nums">{e.ps_qual_code ?? '—'}</td>
                <td className="p-2.5 text-right text-slate-300 tabular-nums">{e.ps_survey_count}</td>
                <td className="p-2.5 text-slate-400 text-xs max-w-md truncate" title={e.rfg_qual_names.join(', ')}>
                  {e.rfg_qual_names.join(', ') || '—'}
                </td>
                <td className="p-2.5 text-right text-slate-300 tabular-nums">{e.rfg_survey_count}</td>
                <td className="p-2.5 text-right text-white font-medium tabular-nums">{e.total_survey_count}</td>
                <td className={`p-2.5 text-right font-medium tabular-nums ${coverageColor(e.member_coverage_pct)}`}>
                  {e.member_coverage_pct.toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tab 3 — Member Lookup ────────────────────────────────────────────────────

const SOURCE_BADGE: Record<string, string> = {
  explicit:    'bg-emerald-900/50 text-emerald-300',
  onboarding:  'bg-emerald-900/50 text-emerald-300',
  prequal:     'bg-blue-900/50 text-blue-300',
  ps_passback: 'bg-purple-900/50 text-purple-300',
  inferred:    'bg-amber-900/50 text-amber-300',
};

function MemberLookupTab() {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState<string | null>(null);
  const { data, error, isFetching } = useQuery({
    queryKey: ['admin', 'pi', 'member', submitted],
    queryFn: () => fetchInferenceChain(submitted!),
    enabled: !!submitted,
    retry: false,
  });

  const grouped = data?.attributes.reduce((acc, a) => {
    (acc[a.source] ??= []).push(a);
    return acc;
  }, {} as Record<string, AttributeWithProvenance[]>) ?? {};

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => { e.preventDefault(); setSubmitted(query.trim() || null); }}
        className="flex gap-2"
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Member email or ID"
          className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
        />
        <button type="submit" className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-lg">Look up</button>
      </form>

      {isFetching && <div className="text-slate-400 text-sm">Loading…</div>}
      {error && <div className="text-red-400 text-sm">Member not found.</div>}

      {data && (
        <>
          <div className="flex flex-wrap gap-3 text-xs text-slate-400">
            <span><span className="text-white font-medium">{data.attributes.length}</span> total</span>
            {Object.entries(grouped).map(([src, rows]) => (
              <span key={src}>
                <span className={`inline-block w-2 h-2 rounded-full mr-1 align-middle ${SOURCE_BADGE[src]?.split(' ')[0] ?? 'bg-slate-500'}`} />
                {rows.length} {src}
              </span>
            ))}
          </div>

          {Object.entries(grouped).map(([src, rows]) => (
            <div key={src} className="bg-slate-800 rounded-xl overflow-hidden">
              <div className={`px-3 py-2 text-[10px] uppercase tracking-wide font-medium ${SOURCE_BADGE[src] ?? 'bg-slate-700 text-slate-400'}`}>
                {src} ({rows.length})
              </div>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-700/60">
                  {rows.map((a) => (
                    <tr key={a.attribute_key} className="hover:bg-slate-700/30">
                      <td className="p-2.5 text-white font-mono text-xs w-1/3">{a.attribute_key}</td>
                      <td className="p-2.5 text-slate-200 text-sm">{a.attribute_value}</td>
                      <td className="p-2.5 text-xs text-slate-400">
                        {a.cross_exchange_value > 0 && (
                          <span title={`${a.cross_exchange_value} live surveys require this attribute`}>
                            🎯 {a.cross_exchange_value}
                          </span>
                        )}
                      </td>
                      <td className="p-2.5 text-xs text-slate-500" title={a.inference_reason ?? undefined}>
                        {a.inference_reason ?? new Date(a.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ── Tab 4 — Custom Questions ─────────────────────────────────────────────────

function CustomQuestionsTab() {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'pi', 'custom-questions'], queryFn: fetchCustomQuestions });
  const [showForm, setShowForm] = useState(false);

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CustomProfileQuestion> }) => patchCustomQuestion(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'pi', 'custom-questions'] }),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteCustomQuestion(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'pi', 'custom-questions'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-400">{data?.entries.length ?? 0} question{data?.entries.length === 1 ? '' : 's'}</p>
        <button
          onClick={() => setShowForm(true)}
          className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-lg"
        >+ New Question</button>
      </div>

      <div className="bg-slate-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/50 text-[10px] uppercase tracking-wide text-slate-400">
            <tr>
              <th className="text-left p-2.5 font-medium">Question</th>
              <th className="text-left p-2.5 font-medium">Attribute</th>
              <th className="text-left p-2.5 font-medium">Type</th>
              <th className="text-right p-2.5 font-medium">Priority</th>
              <th className="text-left p-2.5 font-medium">Exchanges</th>
              <th className="text-left p-2.5 font-medium">Active</th>
              <th className="text-right p-2.5 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/60">
            {data?.entries.map((q) => (
              <tr key={q.id} className="hover:bg-slate-700/30">
                <td className="p-2.5 text-white text-xs max-w-xs truncate" title={q.question_text}>{q.question_text}</td>
                <td className="p-2.5 text-slate-300 font-mono text-xs">{q.attribute_key}</td>
                <td className="p-2.5 text-slate-300 text-xs">{q.question_type}</td>
                <td className="p-2.5 text-right text-white tabular-nums">{q.priority}</td>
                <td className="p-2.5 text-slate-400 text-xs">{q.exchanges.join(', ') || 'all'}</td>
                <td className="p-2.5">
                  <button
                    onClick={() => patchMut.mutate({ id: q.id, body: { active: !q.active } })}
                    className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                      q.active ? 'bg-emerald-900/50 text-emerald-300' : 'bg-slate-700 text-slate-400'
                    }`}
                  >{q.active ? 'Active' : 'Inactive'}</button>
                </td>
                <td className="p-2.5 text-right whitespace-nowrap">
                  <button
                    onClick={() => delMut.mutate(q.id)}
                    className="text-xs text-slate-400 hover:text-red-400"
                  >Delete</button>
                </td>
              </tr>
            ))}
            {data && data.entries.length === 0 && (
              <tr><td colSpan={7} className="p-4 text-center text-slate-500 text-xs">No custom questions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && <NewQuestionForm onClose={() => setShowForm(false)} />}
    </div>
  );
}

function NewQuestionForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    question_text: '',
    attribute_key: '',
    attribute_label: '',
    question_type: 'buttons' as 'buttons' | 'multi_select',
    priority: 5,
    exchanges: ['ps', 'rfg'] as string[],
    is_sensitive: false,
    min_trust_level: 1,
  });
  const [options, setOptions] = useState<Array<{ label: string; value: string }>>([
    { label: '', value: '' }, { label: '', value: '' },
  ]);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => createCustomQuestion({ ...form, options }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pi', 'custom-questions'] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  });

  const attrKeyValid = /^[a-z][a-z0-9_]*$/.test(form.attribute_key);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/50">
      <div className="bg-slate-900 w-full max-w-lg p-6 overflow-y-auto space-y-4 border-l border-slate-700">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold text-white">New Custom Question</h2>
          <button onClick={onClose} className="text-slate-400 text-sm">Close</button>
        </div>

        <div className="space-y-3">
          <Field label="Question text">
            <textarea
              value={form.question_text}
              onChange={(e) => setForm({ ...form, question_text: e.target.value })}
              rows={2}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
              placeholder="What is your favourite cuisine?"
            />
          </Field>

          <Field label="Attribute key (snake_case)">
            <input
              value={form.attribute_key}
              onChange={(e) => setForm({ ...form, attribute_key: e.target.value })}
              className={`w-full bg-slate-800 border rounded-lg px-3 py-2 text-sm text-white font-mono ${
                form.attribute_key && !attrKeyValid ? 'border-red-500' : 'border-slate-700'
              }`}
              placeholder="favourite_cuisine"
            />
            {form.attribute_key && !attrKeyValid && (
              <p className="text-[11px] text-red-400 mt-1">Must be snake_case (start with letter, lowercase + underscores).</p>
            )}
          </Field>

          <Field label="Attribute label">
            <input
              value={form.attribute_label}
              onChange={(e) => setForm({ ...form, attribute_label: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
              placeholder="Favourite cuisine"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select
                value={form.question_type}
                onChange={(e) => setForm({ ...form, question_type: e.target.value as 'buttons' | 'multi_select' })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="buttons">Buttons (single)</option>
                <option value="multi_select">Multi-select</option>
              </select>
            </Field>
            <Field label="Priority (1-10)">
              <input
                type="number" min={1} max={10}
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value, 10) || 5 })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
              />
            </Field>
          </div>

          <Field label="Options">
            {options.map((o, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  value={o.label}
                  onChange={(e) => setOptions(options.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))}
                  placeholder="Label"
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white"
                />
                <input
                  value={o.value}
                  onChange={(e) => setOptions(options.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))}
                  placeholder="value"
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white font-mono"
                />
                {options.length > 2 && (
                  <button onClick={() => setOptions(options.filter((_, idx) => idx !== i))} className="text-slate-400 text-sm">×</button>
                )}
              </div>
            ))}
            <button
              onClick={() => setOptions([...options, { label: '', value: '' }])}
              className="text-xs text-primary"
            >+ Add option</button>
          </Field>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={form.is_sensitive} onChange={(e) => setForm({ ...form, is_sensitive: e.target.checked })} />
              Sensitive (trust ≥ 3 only)
            </label>
          </div>

          <Field label="Exchanges (leave empty for all)">
            <div className="flex gap-2">
              {['ps', 'rfg', 'cint'].map((x) => (
                <label key={x} className="flex items-center gap-1 text-xs text-slate-300">
                  <input
                    type="checkbox"
                    checked={form.exchanges.includes(x)}
                    onChange={(e) => setForm({
                      ...form,
                      exchanges: e.target.checked
                        ? [...form.exchanges, x]
                        : form.exchanges.filter((y) => y !== x),
                    })}
                  /> {x.toUpperCase()}
                </label>
              ))}
            </div>
          </Field>

          {error && <p className="text-sm text-red-400">{error}</p>}

          {/* Live preview */}
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <p className="text-[10px] uppercase text-slate-500 mb-2">Preview</p>
            <p className="text-sm text-white mb-2">{form.question_text || 'Question text appears here…'}</p>
            <div className="flex flex-wrap gap-2">
              {options.filter((o) => o.label).map((o, i) => (
                <span key={i} className="px-2 py-1 rounded bg-slate-700 text-xs text-slate-200">{o.label}</span>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="text-sm text-slate-400 px-4 py-2">Cancel</button>
            <button
              onClick={() => mut.mutate()}
              disabled={!attrKeyValid || form.question_text.length < 10 || options.filter((o) => o.label && o.value).length < 2}
              className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-40"
            >Create</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}
